define((require) => {
    "use strict";

    var _           = require("underscore"),
        Backbone    = require("backbone"),
        {Logger}    = require("shared/util/Logger"),
        Promise     = require("shared/util/Promise");

    var log = new Logger("FilteringRouter");

    var warn = function(route) {
        log.info("Pre-route filter blocked routing of '" + route + "'.");
    };

    /**
     * An Backbone.Router subclass that allows a filter function to be run before each route is dispatched.
     *
     * The filter should be set up as the "before" property, and return a Promise for the route dispatch
     * being allowed, or exactly false if the route dispatch should be blocked.
     *
     * If you need an "after" filter, just listen to Backbone.history.on("route", ...) .
     *
     * @author Bo Gotthardt
     * @class FilteringRouter
     */
    var FilteringRouter = Backbone.Router.extend({

        _routeOwner: null,

        /**
         * Keeps track of the last successful route, so we can restore it when we block a route.
         * @type {String}
         */
        lastSuccessfulRoute: null,

        /**
         * The previous route fragment.
         *
         * @type {String}
         */
        previousRoute: null,

        /**
         * @private
         *
         * Invokes the callback, and store the return value in the <tt>_routeOwner</tt> field.
         *
         * If the "route owner" contains an <tt>onClose</tt> method, this will be invoked when the route owner changes.
         *
         * @param  {Function|String} callback The route callback or function name.
         * @param  {Array}   args     Arguments
         */
        _handleCallback: function(callback, args) {
            var func = _.isFunction(callback) ? callback : this[callback],
                routeOwner = func.apply(this, args);

            if (this._routeOwner) {
                // It might be a Marionette.Controller or an object exposing a onClose callback...
                var closeCallback = this._routeOwner.close || this._routeOwner.onClose;

                if (closeCallback) {
                    // If the route owner has changed, close the previous
                    if (this._routeOwner !== routeOwner) {
                        closeCallback.call(this._routeOwner);
                    }

                    this._routeOwner = null;
                }
            }

            this._routeOwner = routeOwner;
        },

        route: function(route, callback) {
            if (this.before) {
                var newCallback = function() {
                    var args = _.toArray(arguments),
                        blockRoute = this.before.apply(this, [route].concat(args));

                    this.previousRoute = this.lastSuccessfulRoute;

                    if (Promise.isPromise(blockRoute)) {
                        blockRoute
                            .done(() => {
                                this.lastSuccessfulRoute = Backbone.history.fragment;
                                this._handleCallback(callback, args);
                            })
                            .fail(() => {
                                warn(route);
                            });
                    } else {
                        /* eslint-disable no-lonely-if */
                        if (blockRoute === false) {
                            warn(route);
                        } else {
                            this.lastSuccessfulRoute = Backbone.history.fragment;
                            this._handleCallback(callback, args);
                        }
                    }
                }.bind(this);

                Backbone.Router.prototype.route.call(this, route, newCallback);
            } else {
                Backbone.Router.prototype.route.call(this, route, callback);
            }
        }

    }, {

        /**
         * Convenience for navigating to the specified path - delegating to <tt>Backbone.history.navigate</tt>.<p>
         *
         * @param  {String} path The path to navigate to.
         * @param  {Object} [options] Options passed to <tt>Backbone.history.navigate</tt>, e.g. <tt>{trigger: true, replace: true}</tt>.
         */
        navigate: function(path, options) {
            Backbone.history.navigate(path, options);
        },

        /**
         * The navigation call is wrapped in a <tt>defer</tt> (setTimeout of 0) to break out of the current callstack
         * to avoid messing with the router state handled in the <tt>FilteringRouter</tt>.
         *
         * @param  {String} path The path to navigate to.
         * @param  {Object} options Options passed to <tt>Backbone.history.navigate</tt>, e.g. <tt>{trigger: true, replace: true}</tt>.
         */
        navigateLater: function(path, options) {
            _.defer(FilteringRouter.navigate.bind(FilteringRouter.navigate, path, options));
        }

    });

    return FilteringRouter;
});



// WEBPACK FOOTER //
// ./src/editor/zbone/FilteringRouter.js